// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import HqAcceptCompanyInvitation from "./components/HqAcceptCompanyInvitation";
import LOADEvoHQLogin from "./components/LOADEvoHQLogin";
import Loads from './components/Loads';
import Trucks from './components/Trucks';
import Drivers from './components/Drivers';
import CreateLoad from './components/CreateLoad';
import SuperImposableLoad from './components/SuperImposableLoad';
import AssignTruck from './components/AssignTruck';
import AssignTrailer from './components/AssignTrailer';
import AssignDriver from './components/AssignDriver';
import AssignOptions from './components/AssignOptions';
import EditLoad from "./components/EditLoad";
import EditNormalLoad from "./components/EditNormalLoad";
import EditLoadBearer from "./components/EditLoadBearers";
import EditSuperImposableLoad from "./components/EditSuperImposableLoad";
import LoadDetails from './components/LoadDetails';
import TransportSessionLocations from './components/TransportSessionLocations';
import CreateTruck from './components/CreateTruck';
import CreateTrailer from './components/CreateTrailer';
import CreateDriver from './components/CreateDriver';
import Clients from './components/Clients';
import CreateCarrier from './components/CreateCarrier';
import CarrierDetails from './components/CarrierDetails';
import EditCarrier from './components/EditCarrier';
import CreateIndustry from './components/CreateIndustry';
import IndustryDetails from './components/IndustryDetails';
import EditIndustry from './components/EditIndustry';
import Layout from './components/Layout';
import EditDriver from './components/EditDriver';
import DriverDetails from './components/DriverDetails';
import DriverNicknameInput from './components/DriverNicknameInput';
import EditTruck from './components/EditTruck';
import TruckDetails from './components/TruckDetails';
import TrailerDetails from './components/TrailerDetails';
import EditTrailer from './components/EditTrailer';
import Places from './components/Places'; //
import CreatePlace from './components/CreatePlace';
import PlaceDetails from './components/PlaceDetails';
import EditPlace from './components/EditPlace';
import DriverOriginal from './components/DriverOriginal';
import PendentLoads from './components/PendentLoads'; // ← ajusta o caminho se necessário
import PendentLoadDetails from "./components/PendentLoadDetails";
import CreatePendentLoad from './components/CreatePendentLoad'; // ✅ IMPORT
import EditPendentLoad from "./components/EditPendentLoad";
import EditSpecialLoad from "./components/EditSpecialLoad"; // Ajusta o caminho se for diferente
import EditPendentLoadBearer from './components/EditPendentLoadBearer';
import EditPendentSpecialLoad from "./components/EditPendentSpecialLoad";
import LoadHistory from './components/LoadHistory';
import LoadHistoryDetails from './components/LoadHistoryDetails';
import EditLoadHistory from './components/EditLoadHistory';
import Invoices from "./components/Invoices";
import CreateInvoice from "./components/CreateInvoice";
import InvoiceDetails from "./components/InvoiceDetails";
import EditInvoice from "./components/EditInvoice";
import CreatePurchaseOrders from "./components/CreatePurchaseOrders";
import EditPurchaseOrders from "./components/EditPurchaseOrders";
import GeneralSettings from "./pages/GeneralSettings";
import CompanySettings from "./pages/CompanySettings";
import ProfileSettings from "./pages/ProfileSettings";
import UsersSettings from "./pages/UsersSettings";
import PermissionsSettings from "./pages/PermissionsSettings";
import SettingsLayout from "./components/SettingsLayout";

import PersonnelLayout from "./components/personnel/PersonnelLayout";

import PersonnelCalendarPage from "./pages/personnel/PersonnelCalendarPage";
import PersonnelRequestsPage from "./pages/personnel/PersonnelRequestsPage";
import PersonnelPublicHolidaysPage from "./pages/personnel/PersonnelPublicHolidaysPage";
import PersonnelCompensationPage from "./pages/personnel/PersonnelCompensationPage";

import PlansSubscription from "./pages/PlansSubscription";
import SettingsInvoicesStatements from "./pages/SettingsInvoicesStatements";
import CreateUserModalPage from "./pages/CreateUserModalPage";
import { wirePermissionsRealtime } from "./lib/permissions-bus";
import DispatcherNotifications from "./components/DispatcherNotifications";
import GlobalNotificationListener from "./components/GlobalNotificationListener";
import Dashboard from "./pages/Dashboard";

import {
  getStoredToken,
  clearStoredAuth,
} from "./lib/authStorage";

import usePersonnelPermissions from "./hooks/usePersonnelPermissions";

import {
  clearCompanySettingsCache,
  prefetchCompanySettingsData,
  readCompanySettingsCache,
} from "./utils/companySettingsCache";

import {
  clearPersonnelCache,
} from "./utils/personnelCache";

import MapPage from './pages/MapPage';
import LiveTransportsMap from './components/maps/LiveTransportsMap';
import RoutesMap from './components/maps/RoutesMap';
import { canManageRouteTemplates } from "./lib/routePermissions";
import {
  applyStoredAppearance,
  bindSystemAppearanceListener,
} from "./utils/appearancePreference";
import './App.css';

applyStoredAppearance();

function RouteTemplateManagerRoute({
  children,
}) {
  if (
    !canManageRouteTemplates()
  ) {
    return (
      <Navigate
        to="/map/live"
        replace
      />
    );
  }

  return children;
}

function PersonnelAccessRoute({
  children,
}) {
  const {
    permissionsLoading,
    hasAnyView,
  } =
    usePersonnelPermissions();

  if (
    permissionsLoading
  ) {
    return null;
  }

  if (
    !hasAnyView
  ) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return children;
}

function PersonnelPermissionRoute({
  permission,
  children,
}) {
  const {
    permissionsLoading,
    can,
    firstAllowedPath,
  } =
    usePersonnelPermissions();

  if (
    permissionsLoading
  ) {
    return null;
  }

  if (
    !can(
      permission
    )
  ) {
    return (
      <Navigate
        to={
          firstAllowedPath
        }
        replace
      />
    );
  }

  return children;
}

function PersonnelIndexRoute() {
  const {
    permissionsLoading,
    hasAnyView,
    firstAllowedPath,
  } =
    usePersonnelPermissions();

  if (
    permissionsLoading
  ) {
    return null;
  }

  if (
    !hasAnyView
  ) {
    return (
      <Navigate
        to="/dashboard"
        replace
      />
    );
  }

  return (
    <Navigate
      to={
        firstAllowedPath
      }
      replace
    />
  );
}

function App() {
  const storedToken =
    getStoredToken();

  const [
    isLoggedIn,
    setIsLoggedIn,
  ] = useState(
    () =>
      Boolean(
        storedToken
      )
  );

  const [
    companyBootstrapReady,
    setCompanyBootstrapReady,
  ] = useState(
    () =>
      !storedToken ||
      Boolean(
        readCompanySettingsCache()
      )
  );

  useEffect(() => {
    return bindSystemAppearanceListener();
  }, []);

  useEffect(() => {
    let alive = true;

    const bootstrap =
      async () => {
        const token =
          getStoredToken();

        if (!token) {
          if (
            alive
          ) {
            setIsLoggedIn(
              false
            );

            setCompanyBootstrapReady(
              true
            );
          }

          return;
        }

        if (
          alive
        ) {
          setIsLoggedIn(
            true
          );
        }

        const cached =
          readCompanySettingsCache();

        if (
          cached
        ) {
          if (
            alive
          ) {
            setCompanyBootstrapReady(
              true
            );
          }

          prefetchCompanySettingsData()
            .catch(
              (
                error
              ) => {
                console.warn(
                  "[App] Company settings background prefetch failed:",
                  error
                );
              }
            );

          return;
        }

        if (
          alive
        ) {
          setCompanyBootstrapReady(
            false
          );
        }

        try {
          await prefetchCompanySettingsData({
            force:
              true,
          });
        } catch (error) {
          console.warn(
            "[App] Company settings bootstrap failed:",
            error
          );
        } finally {
          if (
            alive
          ) {
            setCompanyBootstrapReady(
              true
            );
          }
        }
      };

    bootstrap();

    return () => {
      alive =
        false;
    };
  }, []);

  useEffect(() => {
    if (
      isLoggedIn
    ) {
      wirePermissionsRealtime();
    }
  }, [
    isLoggedIn,
  ]);

  const handleLoginSuccess =
    async () => {
      try {
        const cached =
          readCompanySettingsCache();

        if (
          !cached
        ) {
          setCompanyBootstrapReady(
            false
          );
        }

        await prefetchCompanySettingsData({
          force:
            true,
        });
      } catch (error) {
        console.warn(
          "[App] Initial company settings prefetch failed:",
          error
        );
      } finally {
        setCompanyBootstrapReady(
          true
        );

        setIsLoggedIn(
          true
        );
      }
    };

  /*
   * Só acontece na primeira utilização absoluta
   * daquele browser/conta, quando ainda não existe cache.
   *
   * Não mostramos a página Empresa vazia para depois
   * a preencher — esperamos primeiro pelos dados.
   */
  if (
    isLoggedIn &&
    !companyBootstrapReady
  ) {
    return (
      <div className="app-container" />
    );
  }

  return (
    <Router>
      <AppContent
        isLoggedIn={
          isLoggedIn
        }
        onLoginSuccess={
          handleLoginSuccess
        }
      />
    </Router>
  );
}

function AppContent({ isLoggedIn, onLoginSuccess }) {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const location = useLocation();
  const isSuperImposableMode = location.pathname === '/loads/create' && location.search.includes('isSuperImposable=true');
  const isSettings =
    location.pathname.startsWith(
      "/settings"
    );

  const isPersonnel =
    location.pathname.startsWith(
      "/personnel"
    );

  // Hide sidebar on certain routes
  useEffect(() => {
    const hideSidebarPaths = ['/clients/create', '/clients/:clientId/edit'];
    const shouldHideSidebar = hideSidebarPaths.some((path) =>
      location.pathname.includes(path)
    );
    setIsSidebarVisible(!shouldHideSidebar);
  }, [location.pathname]);

  const handleLogout = () => {
    /*
     * IMPORTANTE:
     * limpar caches ANTES do auth,
     * porque as respetivas chaves
     * dependem da empresa/utilizador.
     */
    clearCompanySettingsCache();

    clearPersonnelCache();

    clearStoredAuth();

    window.location.reload();
  };

  function CreateUserRouteWrapper() {
    const navigate = useNavigate();
    return (
      <CreateUserModalPage
        isOpen
        onClose={() => navigate("/settings/users")}
        onCreated={() => navigate("/settings/users")}
      />
    );
  }

  if (
    location.pathname ===
    "/accept-invite"
  ) {
    return (
      <div className="app-container">
        <Routes>
          <Route
            path="/accept-invite"
            element={
              <HqAcceptCompanyInvitation />
            }
          />

          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
              />
            }
          />
        </Routes>
      </div>
    );
  }

  return (
    <div className="app-container">
      {isLoggedIn ? (
        <>
          <GlobalNotificationListener />

          {isSidebarVisible && <Sidebar isVisible={isSidebarVisible} onLogout={handleLogout} />}
            <div
              className={[
                "content",

                isSidebarVisible
                  ? ""
                  : "expanded",

                isSettings
                  ? "content--settings"
                  : "",

                isPersonnel
                  ? "content--personnel"
                  : "",

                location.pathname ===
                "/dashboard"
                  ? "content--dashboard"
                  : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
            <Routes key={location.pathname} location={location}>
              {/* ✅ Default landing (quando abres o software em "/") */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              {/* Main Navigation Routes */}
              <Route path="/loads" element={<Loads />} />

              {/* Map Routes */}
              <Route path="/map" element={<MapPage />}>
                <Route
                  index
                  element={
                    <Navigate
                      to="live"
                      replace
                    />
                  }
                />

                <Route
                  path="live"
                  element={
                    <LiveTransportsMap />
                  }
                />

                <Route
                  path="routes"
                  element={
                    <RouteTemplateManagerRoute>
                      <RoutesMap />
                    </RouteTemplateManagerRoute>
                  }
                />
              </Route>

              <Route path="/pendentloads" element={<PendentLoads />} />
              <Route path="/pendentload/create" element={<CreatePendentLoad />} />
              <Route path="/pendentloads/:id/edit" element={<EditPendentLoad />} />
              <Route path="/pendentloads/:loadId/edit-bearer" element={<EditPendentLoadBearer />} />
              <Route path="/pendentloads/:loadId/edit-special" element={<EditPendentSpecialLoad />} />
              <Route path="/pendentloads/:id/details" element={<PendentLoadDetails />} />
              <Route path="/loads/:loadId/details" element={<LoadDetails />} />
              <Route path="/trucks" element={<Trucks />} />
              <Route path="/trucks/:id/details" element={<TruckDetails />} />
              <Route path="/drivers" element={<Drivers />} />
              <Route
                path="/loads/assign-options/:loadId"
                element={<AssignOptions />}
              />

              {/* Personnel */}
              <Route
                path="/personnel"
                element={
                  <PersonnelAccessRoute>
                    <PersonnelLayout />
                  </PersonnelAccessRoute>
                }
              >
                <Route
                  index
                  element={
                    <PersonnelIndexRoute />
                  }
                />

                <Route
                  path="calendar"
                  element={
                    <PersonnelPermissionRoute
                      permission="personnelCalendarView"
                    >
                      <PersonnelCalendarPage />
                    </PersonnelPermissionRoute>
                  }
                />

                <Route
                  path="requests"
                  element={
                    <PersonnelPermissionRoute
                      permission="personnelRequestsView"
                    >
                      <PersonnelRequestsPage />
                    </PersonnelPermissionRoute>
                  }
                />

                <Route
                  path="public-holidays"
                  element={
                    <PersonnelPermissionRoute
                      permission="personnelPublicHolidaysView"
                    >
                      <PersonnelPublicHolidaysPage />
                    </PersonnelPermissionRoute>
                  }
                />

                <Route
                  path="compensation"
                  element={
                    <PersonnelPermissionRoute
                      permission="personnelCompensationView"
                    >
                      <PersonnelCompensationPage />
                    </PersonnelPermissionRoute>
                  }
                />
              </Route>

              {/* Settings (nested) */}
              <Route
                path="/settings"
                element={
                  <SettingsLayout />
                }
              >
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<GeneralSettings />} />
                <Route
                  path="company"
                  element={<CompanySettings />}
                />

                <Route
                  path="company/company-profile"
                  element={
                    <Navigate
                      to="/settings/company#company-profile"
                      replace
                    />
                  }
                />

                <Route
                  path="company/operation-details"
                  element={
                    <Navigate
                      to="/settings/company#operation-details"
                      replace
                    />
                  }
                />

                <Route
                  path="company/contact-information"
                  element={
                    <Navigate
                      to="/settings/company#contact-information"
                      replace
                    />
                  }
                />

                <Route
                  path="company/financial-information"
                  element={
                    <Navigate
                      to="/settings/company#financial-information"
                      replace
                    />
                  }
                />

                <Route
                  path="company/payment-terms"
                  element={
                    <Navigate
                      to="/settings/company#payment-terms"
                      replace
                    />
                  }
                />

                <Route
                  path="company/tax-information"
                  element={
                    <Navigate
                      to="/settings/company#tax-information"
                      replace
                    />
                  }
                />
                <Route
                  path="profile"
                  element={<ProfileSettings />}
                />

                <Route
                  path="profile/personal-info"
                  element={
                    <Navigate
                      to="/settings/profile"
                      replace
                    />
                  }
                />

                <Route
                  path="profile/account-access"
                  element={
                    <Navigate
                      to="/settings/profile"
                      replace
                    />
                  }
                />

                {/* Users list */}
                <Route path="users" element={<UsersSettings />} />

                {/* NEW: Add User page (modal-styled page) */}
                <Route path="users/new" element={<CreateUserRouteWrapper />} />

                <Route path="permissions" element={<PermissionsSettings />} />
                <Route path="plans" element={<PlansSubscription />} />
                <Route path="invoices" element={<SettingsInvoicesStatements />} />
              </Route>

              {/* Optional: keep legacy URLs working via redirects */}
              <Route path="/general-settings" element={<Navigate to="/settings/general" replace />} />
              <Route path="/company-settings" element={<Navigate to="/settings/company" replace />} />
              <Route path="/profile-settings" element={<Navigate to="/settings/profile" replace />} />
              <Route
                path="/company-profile"
                element={
                  <Navigate
                    to="/settings/company#company-profile"
                    replace
                  />
                }
              />

              {/* Client Management Routes */}
              <Route path="/clients" element={<Layout />}>
                <Route index element={<Clients />} />
                <Route path=":view" element={<Clients />} /> {/* Dynamic path for Carriers or Industries */}
                <Route path="create">
                  <Route path="carrier" element={<CreateCarrier />} />
                  <Route path="industry" element={<CreateIndustry />} />
                </Route>
                <Route path="/clients/details/carrier/:carrierId" element={<CarrierDetails />} />
                <Route path="/clients/edit/carrier/:carrierId" element={<EditCarrier />} />
                <Route path="/clients/details/industry/:industryId" element={<IndustryDetails />} />
                <Route path="/clients/edit/industry/:industryId" element={<EditIndustry />} />
              </Route>

              {/* Load Management Routes */}
              <Route
                path="/loads/create"
                element={
                  <CreateLoad
                    isSuperImposableMode={
                      isSuperImposableMode
                    }
                  />
                }
              />

              <Route
                path="/loads/superimposable-create"
                element={
                  <SuperImposableLoad />
                }
              />

              {/* ✅ Novo editor universal de Current Loads */}
              <Route
                path="/loads/:loadId/edit"
                element={
                  <EditLoad />
                }
              />

              {/*
                ============================================================
                LEGACY EDITORS
                Manter temporariamente até validarmos:
                - Normal
                - Special
                - Load Bearer
                - Superimposable
                ============================================================
              */}

              <Route
                path="/loads/:loadId/edit-normal"
                element={
                  <EditNormalLoad />
                }
              />

              <Route
                path="/loads/:loadId/edit-bearer"
                element={
                  <EditLoadBearer />
                }
              />

              <Route
                path="/loads/:loadId/edit-superimposable"
                element={
                  <EditSuperImposableLoad />
                }
              />

              <Route
                path="/loads/:loadId/edit-special"
                element={
                  <EditSpecialLoad />
                }
              />

              <Route
                path="/loads/:loadId/assign-truck"
                element={
                  <AssignTruck />
                }
              />

              <Route
                path="/loads/:loadId/assign-trailer"
                element={
                  <AssignTrailer />
                }
              />

              <Route
                path="/loads/:loadId/assign-driver"
                element={
                  <AssignDriver />
                }
              />
              <Route 
                path="/transport-session/:transportsessionId/locations" 
                element={<TransportSessionLocations />} 
              />

              {/* Load History Routes */}
              <Route path="/load-history" element={<LoadHistory />} />
              <Route path="/load-history/:id/details" element={<LoadHistoryDetails />} />
              <Route path="/load-history/:id/edit" element={<EditLoadHistory />} />

              {/* Truck Management Routes */}
              <Route path="/trucks/create" element={<CreateTruck />} />
              <Route path="/trucks/:truckId/edit" element={<EditTruck />} />
              <Route path="/trucks/:truckId/details" element={<TruckDetails />} />

              {/* Trailer Management Routes */}
              <Route path="/trailers/create" element={<CreateTrailer />} />
              <Route path="/trailers/:trailerId/edit" element={<EditTrailer />} />
              <Route path="/trailers/:id" element={<TrailerDetails />} />

              {/* DriverOriginal (intake) */}
              <Route path="/driveroriginal" element={<DriverOriginal />} />

              {/* Temporary: reuse list component for row navigation target */}
              <Route path="/driveroriginal/:id" element={<DriverOriginal />} />

              {/* Driver Management Routes */}
              <Route path="/drivers/create" element={<CreateDriver />} />
              <Route path="/drivers/new-nickname" element={<DriverNicknameInput />} />
              <Route path="/drivers/:driverId/edit" element={<EditDriver />} />
              <Route path="/drivers/:driverId/details" element={<DriverDetails />} />

              {/* Places Management Routes */}
              <Route path="/places" element={<Layout />}>
                <Route index element={<Places />} />
                <Route path="create" element={<CreatePlace />} />
                <Route path="/places/:placeId/details" element={<PlaceDetails />} />
                <Route path="/places/:placeId/edit" element={<EditPlace />} />
              </Route>

               {/* Invoices Route */}
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/invoices/create" element={<CreateInvoice />} />
              <Route path="/invoices/:invoiceId/details" element={<InvoiceDetails />} />
              <Route path="/invoices/edit/:invoiceId" element={<EditInvoice />} />

              {/* Alias: abrir Purchase Orders dentro da página /invoices */}
              <Route path="/purchaseorders" element={<Navigate to="/invoices?view=purchase_orders" replace />} />
              <Route path="/purchaseorders/create" element={<CreatePurchaseOrders />} />
              <Route path="/purchaseorders/edit/:id" element={<EditPurchaseOrders />} />

              {/* Notifications (dispatcher) */}
              <Route path="/notifications" element={<DispatcherNotifications />} />

              <Route path="/dashboard" element={<Dashboard />} />

              {/* Fallback Route */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </>
      ) : (
        location.pathname.startsWith("/hq") ? (
          <Routes>
            <Route path="/hq/login" element={<LOADEvoHQLogin />} />
            <Route path="*" element={<Navigate to="/hq/login" replace />} />
          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<Login onLoginSuccess={onLoginSuccess} />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="*"
              element={
                <Navigate
                  to="/"
                  replace
                  state={{ from: location.pathname + location.search }}
                />
              }
            />
          </Routes>
        )
      )}
    </div>
  );
}
export default App;